/**
 * useEmpresasMap (rc20+) — hook compartido que carga la lista de empresas
 * (incluyendo inactivas) y devuelve un Map<id, {nombre, activa}> para que
 * las listas de clientes/contratos/facturas pinten un chip "🏢 Empresa X"
 * sin tener que hacer N+1 lookups.
 *
 * Cache 60s. Hace UNA sola llamada a /api/v1/empresas para todo el árbol.
 */
import { useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";

export type EmpresaLite = {
  id: number;
  nombre: string;
  activa: boolean;
};

export function useEmpresasMap() {
  const q = useQuery<EmpresaLite[]>({
    queryKey: ["empresas", "map"],
    queryFn: () => apiCall<EmpresaLite[]>("get", "/api/v1/empresas"),
    staleTime: 60_000,
  });

  const empresas = q.data ?? [];
  const byId = new Map<number, EmpresaLite>();
  for (const e of empresas) byId.set(e.id, e);

  return {
    empresas,
    byId,
    /** Mayor a 1 → vale la pena mostrar la columna/chip */
    multiEmpresa: empresas.length > 1,
    nombreOf: (id: number | null | undefined): string => {
      if (id == null) return "—";
      return byId.get(id)?.nombre ?? `#${id}`;
    },
    isLoading: q.isLoading,
  };
}
